import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  signInWithPopup,
  sendPasswordResetEmail,
  updateProfile,
  RecaptchaVerifier,
  signInWithPhoneNumber
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../firebase/config';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);
  const [recaptchaVerifiers, setRecaptchaVerifiers] = useState({});
  // Profile completion flow state
  const [profileCompletionOpen, setProfileCompletionOpen] = useState(false);
  const [profileDraft, setProfileDraft] = useState(null);
  const [missingFields, setMissingFields] = useState({ fullName: false, phone: false, address: false });

  const REQUIRED_FIELDS = ['fullName', 'phone']; // address is optional by default

  const computeMissing = (data = {}) => {
    const mf = {
      fullName: !((data.fullName || '').trim()),
      phone: !((data.phone || '').trim()),
      address: !((data.address || '').trim()),
    };
    // Only gate on required ones when deciding to open modal
    const hasRequiredMissing = REQUIRED_FIELDS.some((k) => mf[k]);
    return { mf, hasRequiredMissing };
  };

  const openProfileCompletion = (draft) => {
    const { mf } = computeMissing(draft || {});
    setMissingFields(mf);
    setProfileDraft(draft || { fullName: '', phone: '', address: '' });
    setProfileCompletionOpen(true);
  };

  const closeProfileCompletion = () => {
    setProfileCompletionOpen(false);
    setProfileDraft(null);
    setMissingFields({ fullName: false, phone: false, address: false });
  };

  // Register with email and password
  const register = async (email, password, userData) => {
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update profile
      await updateProfile(user, {
        displayName: userData.fullName
      });

      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: user.email,
        fullName: userData.fullName,
        phone: userData.phone,
        address: userData.address || '',
        role: 'customer',
        createdAt: new Date().toISOString()
      });

      toast.success('Account created successfully!');
      return user;
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message);
      throw error;
    }
  };

  // Login with email and password
  const login = async (email, password) => {
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      toast.success('Logged in successfully!');
      return user;
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.message);
      throw error;
    }
  };

  // Login with Google
  const loginWithGoogle = async () => {
    try {
      const { user } = await signInWithPopup(auth, googleProvider);
      
      // Check if user document exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (!userDoc.exists()) {
        // Create user document for new Google users
        await setDoc(doc(db, 'users', user.uid), {
          uid: user.uid,
          email: user.email,
          fullName: user.displayName,
          phone: '',
          address: '',
          role: 'customer',
          createdAt: new Date().toISOString()
        });
      }

      // Load latest profile and check for missing required fields
      const fresh = await getDoc(doc(db, 'users', user.uid));
      const data = fresh.exists() ? fresh.data() : { email: user.email || '', fullName: user.displayName || '', phone: '', address: '' };
      const { hasRequiredMissing } = computeMissing(data);
      if (hasRequiredMissing) {
        openProfileCompletion({
          fullName: data.fullName || user.displayName || '',
          phone: data.phone || '',
          address: data.address || ''
        });
      }

      toast.success('Logged in with Google successfully!');
      return user;
    } catch (error) {
      console.error('Google login error:', error);
      toast.error(error.message);
      throw error;
    }
  };

  // Ensure user document exists (used by social/phone sign-ins)
  const ensureUserDocument = async (user, extraData = {}) => {
    try {
      const userRef = doc(db, 'users', user.uid);
      const snap = await getDoc(userRef);
      if (!snap.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email || '',
          fullName: user.displayName || extraData.fullName || '',
          phone: user.phoneNumber || extraData.phone || '',
          address: extraData.address || '',
          role: 'customer',
          createdAt: new Date().toISOString()
        });
      } else if (Object.keys(extraData).length > 0) {
        // Merge any provided additional data (e.g., name/address from register flow)
        await setDoc(userRef, {
          ...snap.data(),
          ...extraData,
          uid: user.uid
        });
      }
    } catch (error) {
      console.error('ensureUserDocument error:', error);
      throw error;
    }
  };

  // Format Indian phone numbers to E.164 (+91XXXXXXXXXX) if needed
  const toE164 = (phone) => {
    const trimmed = (phone || '').replace(/\s|-/g, '');
    if (trimmed.startsWith('+')) return trimmed; // already E.164
    // Assume India if 10 digits
    if (/^[6-9]\d{9}$/.test(trimmed)) return `+91${trimmed}`;
    return trimmed; // fallback
  };

  // Send OTP to phone (creates/uses a recaptcha instance bound to given container id)
  const sendOtp = async (phone, recaptchaContainerId) => {
    try {
      if (!recaptchaContainerId) throw new Error('Missing reCAPTCHA container id');

      // Reuse or create a verifier per container id
      let verifier = recaptchaVerifiers[recaptchaContainerId];
      if (!verifier) {
        // Use compatible signature: new RecaptchaVerifier(containerId, parameters, auth)
        verifier = new RecaptchaVerifier(recaptchaContainerId, { size: 'invisible' }, auth);
        await verifier.render();
        setRecaptchaVerifiers((prev) => ({ ...prev, [recaptchaContainerId]: verifier }));
      }

      const e164 = toE164(phone);
      const confirmationResult = await signInWithPhoneNumber(auth, e164, verifier);
      toast.success('OTP sent successfully');
      return confirmationResult; // caller should keep this reference for confirm
    } catch (error) {
      console.error('sendOtp error:', error);
      toast.error(error.message || 'Failed to send OTP');
      throw error;
    }
  };

  // Verify OTP using confirmationResult from sendOtp
  const verifyOtp = async (confirmationResult, otp, profileData = null) => {
    try {
      if (!confirmationResult) throw new Error('Missing confirmation result');
      const { user } = await confirmationResult.confirm(otp);

      // If profile data provided (e.g., from Register), set display name
      if (profileData?.fullName) {
        try { await updateProfile(user, { displayName: profileData.fullName }); } catch {}
      }

      await ensureUserDocument(user, {
        fullName: profileData?.fullName || user.displayName || '',
        phone: user.phoneNumber || profileData?.phone || '',
        address: profileData?.address || ''
      });

      // Check if any required details are still missing (e.g., name when logging in via phone from Login page)
      const fresh = await getDoc(doc(db, 'users', user.uid));
      const data = fresh.exists() ? fresh.data() : { fullName: user.displayName || '', phone: user.phoneNumber || '', address: '' };
      const { hasRequiredMissing } = computeMissing(data);
      if (hasRequiredMissing) {
        openProfileCompletion({
          fullName: data.fullName || user.displayName || '',
          phone: data.phone || user.phoneNumber || '',
          address: data.address || ''
        });
      }

      toast.success('Phone sign-in successful');
      return user;
    } catch (error) {
      console.error('verifyOtp error:', error);
      toast.error(error.message || 'Invalid OTP');
      throw error;
    }
  };

  // Save completed profile details
  const completeProfile = async (data) => {
    try {
      if (!currentUser) throw new Error('No authenticated user');
      const userRef = doc(db, 'users', currentUser.uid);
      // Merge provided fields
      await setDoc(userRef, {
        uid: currentUser.uid,
        ...data
      }, { merge: true });

      // Update display name if provided
      if (data.fullName) {
        try { await updateProfile(currentUser, { displayName: data.fullName }); } catch {}
      }

      // Refresh local profile state
      await fetchUserProfile(currentUser.uid);
      closeProfileCompletion();
      toast.success('Profile updated');
    } catch (error) {
      console.error('completeProfile error:', error);
      toast.error(error.message || 'Failed to save profile');
      throw error;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      toast.success('Logged out successfully!');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(error.message);
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent!');
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(error.message);
      throw error;
    }
  };

  // Fetch user profile
  const fetchUserProfile = async (uid) => {
    try {
      const userDoc = await getDoc(doc(db, 'users', uid));
      if (userDoc.exists()) {
        setUserProfile(userDoc.data());
        return userDoc.data();
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  // Check if user is admin
  const isAdmin = () => {
    return userProfile?.role === 'admin';
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    // profile completion
    profileCompletionOpen,
    profileDraft,
    missingFields,
    openProfileCompletion,
    closeProfileCompletion,
    completeProfile,
    register,
    login,
    loginWithGoogle,
    sendOtp,
    verifyOtp,
    logout,
    resetPassword,
    fetchUserProfile,
    isAdmin
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};